import * as Sentry from '@sentry/nextjs'
import hash from 'stable-hash'
import memoize from 'lodash-es/memoize'
import { isString } from 'lodash-es'
import { backOff } from 'exponential-backoff'

import { NextBillionReverseGeocodeResponse } from '../types/NextBillion.types'

const nextBillionReverseGeocode = async (lat: number, lng: number): Promise<string | undefined> => {
  const apiKey = process.env.NEXT_PUBLIC_NEXTBILLION_API_KEY

  if (!apiKey) {
    throw new Error('NextBillion.ai API key not configured')
  }

  try {
    const response = await fetch(
      `https://api.nextbillion.io/revgeocode?at=${lat},${lng}&key=${apiKey}`,
      {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      },
    )

    if (!response.ok) {
      throw new Error(`NextBillion.ai API error: ${response.status} ${response.statusText}`)
    }

    const data: NextBillionReverseGeocodeResponse = await response.json()

    if (data.items && data.items.length > 0) {
      return data.items[0].title
    }
  } catch (error) {
    Sentry.captureException(error)
    throw error
  }
}

const googleReverseGeocode = async (lat: number, lng: number): Promise<string | undefined> => {
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
    return undefined
  }

  try {
    // set up back-off with exponential retries (up to 5) and jitter if a request fails
    // the first retry is after 250ms, thereafter n * 2 * 250 where n is attempt number
    const response = await backOff(
      () => new window.google.maps.Geocoder().geocode({ location: { lat, lng } }),
      {
        jitter: 'full',
        numOfAttempts: 5,
        startingDelay: 250,
        timeMultiple: 2,
        retry: (e, attempt) => {
          console.debug(`${new Date().toISOString()}: Retrying geocoding, attempt ${attempt}`)
          return true
        },
      },
    )

    const results = response?.results
    if (results && !isString(results) && results.length > 0) {
      return results[0].formatted_address
    }
  } catch (error) {
    console.error(error)
    Sentry.captureException(error)
    throw error
  }
}

const memoizedReverseGeocode = memoize(
  async (lat: number, lng: number, geocodeMode: string) => {
    switch (geocodeMode) {
      case 'NextBillion':
        return nextBillionReverseGeocode(lat, lng)

      case 'Comparison':
        const [nextBillionResult, googleResult] = await Promise.all([
          nextBillionReverseGeocode(lat, lng),
          googleReverseGeocode(lat, lng),
        ])
        Sentry.logger.info('Reverse geocode results', {
          nextBillion: nextBillionResult,
          google: googleResult,
        })
        return googleResult

      default:
        return await googleReverseGeocode(lat, lng)
    }
  },
  (lat, lng, geocodeMode) => hash([lat, lng, geocodeMode]),
)

export const reverseGeocode = async (
  lat: number,
  lng: number,
  geocodeMode: string,
): Promise<string | undefined> => {
  const cacheKey = hash([lat, lng, geocodeMode])

  try {
    // we unwrap the promise here, so that if it represents a failure
    // we can remove it from the cache. that is don so subsequent calls will
    // try again, instead of returning the memoized, failed, promise
    return await memoizedReverseGeocode(lat, lng, geocodeMode)
  } catch (error) {
    console.debug(`removing memoized geocode result for key ${cacheKey}`)
    memoizedReverseGeocode.cache.delete(cacheKey)
    throw error
  }
}
